/* Bible access layer.
 *
 * Strategy:
 *   1. Try bible-api.com (translation=almeida, public-domain Portuguese).
 *   2. On success, cache the chapter in localStorage (cm_bible_cache_v1).
 *   3. On failure (offline, 5xx, timeout), fall back to the locally bundled
 *      bible.json for the 3 books that still ship with the app.
 *   4. When neither source has the requested chapter, raise a typed error so
 *      the UI can show a friendly "offline / indisponível" message.
 *
 * Book metadata (all 66 books) comes from data/bibleBooks.js. Legacy helpers
 * consumed by the existing screens (findBook / neighbourChapters / chapterHref)
 * are preserved below so Biblia*.jsx keep working without sweeping changes.
 */

import localData from "../data/bible.json";
import { BIBLE_BOOKS, findBookBySlug } from "../data/bibleBooks";

// ---- Constants ---------------------------------------------------------------
const API_BASE = "https://bible-api.com";
const TRANSLATION = "almeida";
const CACHE_KEY = "cm_bible_cache_v1";
const CACHE_VERSION = 1;
const TIMEOUT_MS = 8000;

// ---- Version info ------------------------------------------------------------
export const getCurrentVersion = () => ({
  code: "almeida",
  name: "Almeida",
  subtitle: "domínio público",
});

export const getAvailableVersions = () => [
  { code: "almeida", name: "Almeida", enabled: true },
];

// ---- Books listing -----------------------------------------------------------
export const listBooks = () =>
  BIBLE_BOOKS.map((b) => ({
    slug: b.slug,
    name: b.name,
    abbreviation: b.abbr,
    testament: b.testament === "AT" ? "Antigo Testamento" : "Novo Testamento",
    testament_code: b.testament,
    total_chapters: b.chapters,
  }));

export const findBook = (slug) => {
  const b = findBookBySlug(slug);
  if (!b) return null;
  return {
    slug: b.slug,
    name: b.name,
    abbreviation: b.abbr,
    testament: b.testament === "AT" ? "Antigo Testamento" : "Novo Testamento",
    total_chapters: b.chapters,
  };
};

export const neighbourChapters = (slug, chapterNumber) => {
  const book = findBookBySlug(slug);
  if (!book) return { prev: null, next: null, totalChapters: 0 };
  const num = Number.parseInt(chapterNumber, 10);
  return {
    prev: num > 1 ? num - 1 : null,
    next: num < book.chapters ? num + 1 : null,
    totalChapters: book.chapters,
  };
};

export const chapterHref = (slug, num) => `/biblia/${slug}/${num}`;
export const bookHref = (slug) => `/biblia/${slug}`;

// ---- Cache -------------------------------------------------------------------
const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== CACHE_VERSION) return {};
    return parsed.data || {};
  } catch {
    return {};
  }
};

const writeCache = (data) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ v: CACHE_VERSION, data }));
  } catch {
    /* quota exceeded, ignore */
  }
};

const cacheKey = (slug, chapter) => `${slug}:${chapter}`;

// ---- Local fallback ----------------------------------------------------------
const findLocalChapter = (slug, chapter) => {
  const book = (localData.books || []).find((b) => b.slug === slug);
  if (!book) return null;
  const num = Number.parseInt(chapter, 10);
  const ch = (book.chapters || []).find((c) => c.number === num);
  if (!ch || !Array.isArray(ch.verses) || !ch.verses.length) return null;
  return {
    book_name: book.name,
    book_slug: book.slug,
    chapter: num,
    verses: ch.verses.map((v) => ({ verse: v.number, text: v.text })),
    source: "local",
  };
};

// ---- API fetch ---------------------------------------------------------------
const fetchWithTimeout = (url, ms = TIMEOUT_MS) =>
  new Promise((resolve, reject) => {
    const controller = new AbortController();
    const t = setTimeout(() => {
      controller.abort();
      reject(new Error("timeout"));
    }, ms);
    fetch(url, { signal: controller.signal })
      .then((r) => {
        clearTimeout(t);
        resolve(r);
      })
      .catch((err) => {
        clearTimeout(t);
        reject(err);
      });
  });

const normalizeApiResponse = (raw, book, chapter) => {
  if (!raw || !Array.isArray(raw.verses)) return null;
  return {
    book_name: book?.name || raw.verses[0]?.book_name || "",
    book_slug: book?.slug || "",
    chapter: Number.parseInt(chapter, 10),
    verses: raw.verses.map((v) => ({
      verse: v.verse,
      text: (v.text || "").trim(),
    })),
    source: "api",
  };
};

/**
 * Fetches a full chapter (all verses) for the given book slug.
 * Returns: { book_name, book_slug, chapter, verses:[{verse, text}], source }
 * Throws: Error('not-found') | Error('offline') | Error('unavailable')
 */
export async function fetchChapter(slug, chapter) {
  const book = findBookBySlug(slug);
  if (!book) throw new Error("not-found");
  const num = Number.parseInt(chapter, 10);
  if (!num || num < 1 || num > book.chapters) throw new Error("not-found");

  // 1. Cache hit
  const cache = readCache();
  const key = cacheKey(slug, num);
  if (cache[key]) {
    return { ...cache[key], source: "cache" };
  }

  // 2. Try API
  try {
    const url = `${API_BASE}/${encodeURIComponent(book.apiSlug)}+${num}?translation=${TRANSLATION}`;
    const res = await fetchWithTimeout(url);
    if (res.ok) {
      const data = await res.json();
      const normalized = normalizeApiResponse(data, { slug: book.slug, name: book.name }, num);
      if (normalized && normalized.verses.length) {
        const nextCache = { ...cache, [key]: normalized };
        writeCache(nextCache);
        return normalized;
      }
    }
  } catch {
    /* silent, fall through to local. CORS / 5xx / timeout are expected
       under bulk download; the OfflineBibleDownloader counts failures and
       reports them in aggregate. */
  }

  // 3. Local bundled fallback (João / Romanos / Salmos, the 3 MVP books)
  const local = findLocalChapter(slug, num);
  if (local) return local;

  // 4. Nothing available
  throw new Error("unavailable");
}

/** Fetches just one verse (single-verse card). Respects the same cache. */
export async function fetchVerse(slug, chapter, verse) {
  const ch = await fetchChapter(slug, chapter);
  const found = ch.verses.find((v) => v.verse === verse);
  return found ? { ...found, book_name: ch.book_name, chapter: ch.chapter } : null;
}
